import React, { useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { INPUT_STYLE_TYPES } from '@CommonConstants';

import { createAuthor } from '@Services/User/Request';
import { removeAllUserState, removeUserInLocalStorage } from '@Services/User';

import TX from '@Components/Common/Text';
import Input from '@Components/Common/Input';
import Button from '@Components/Common/Button';
import SnackBar from '@Components/Common/SnackBar';

import { ReactComponent as DeleteSVG } from 'Assets/Svgs/ic_delete.svg';

const NICKNAME_CREATE_ERROR_MESSAGE = {
  VALIDATION_FAILED: 'validation failed',
  EXIST: 'exist',
  DUPLICATE: 'duplicate',
  IMPROPER: 'improper',
};

// TODO: 윤지한테 이미 존재하는 사용자일 때 어떻게 할 건지 물어보기!
const SNACKBAR_ERROR_MESSAGE = {
  [NICKNAME_CREATE_ERROR_MESSAGE.VALIDATION_FAILED]: '한글, 영문, 숫자, 띄어쓰기만 입력해 주세요.',
  [NICKNAME_CREATE_ERROR_MESSAGE.EXIST]: '',
  [NICKNAME_CREATE_ERROR_MESSAGE.DUPLICATE]: '사용 중인 닉네임입니다. 다른 닉네임을 입력해 주세요.',
  [NICKNAME_CREATE_ERROR_MESSAGE.IMPROPER]: '비속어는 포함하실 수 없습니다.',
};

const Nickname = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const snackBarRef = useRef(null);
  const [nickname, setNickname] = useState('');

  const showSnackBar = text => snackBarRef.current.show({ text });

  const dispatchRemoveAllUserState = useCallback(() => {
    dispatch(removeAllUserState());
  }, [dispatch]);

  const onChangeNickname = useCallback(
    event => {
      const { value } = event.target;

      setNickname(value);
    },
    [nickname],
  );

  const handleNicknameCheck = useCallback(
    event => {
      const { value: inputValue } = event.target;
      console.log('inputValue:', inputValue);
      // eslint-disable-next-line no-useless-escape
      const regExp = /[\{\}\[\]\/?.,;:|\)*~`!^\-_+┼<>@\#$%&\'\"\\\(\=]/;

      if (regExp.test(inputValue)) {
        const beforeInputValue = inputValue.slice(0, -1);

        showSnackBar(SNACKBAR_ERROR_MESSAGE[NICKNAME_CREATE_ERROR_MESSAGE.VALIDATION_FAILED]);
        setNickname(beforeInputValue);
      }
    },
    [nickname],
  );

  const handleCloseButtonClick = () => {
    dispatchRemoveAllUserState();
    removeUserInLocalStorage();

    navigate('/login', { replace: true });
  };

  const handleNicknameCreateButtonClick = async () => {
    try {
      await createAuthor(nickname);
      navigate('/', { replace: true });
    } catch (error) {
      const { msg } = error;

      switch (msg) {
        case NICKNAME_CREATE_ERROR_MESSAGE.EXIST: {
          showSnackBar(SNACKBAR_ERROR_MESSAGE[NICKNAME_CREATE_ERROR_MESSAGE.EXIST]);
          break;
        }

        case NICKNAME_CREATE_ERROR_MESSAGE.DUPLICATE: {
          showSnackBar(SNACKBAR_ERROR_MESSAGE[NICKNAME_CREATE_ERROR_MESSAGE.DUPLICATE]);
          break;
        }

        case NICKNAME_CREATE_ERROR_MESSAGE.IMPROPER: {
          showSnackBar(SNACKBAR_ERROR_MESSAGE[NICKNAME_CREATE_ERROR_MESSAGE.IMPROPER]);
          break;
        }

        default: {
          break;
        }
      }

      setNickname('');
    }
  };

  return (
    <>
      <DeleteButtonIcon onClick={handleCloseButtonClick} />

      <NicknameContainer>
        <TX.Headline2>작가 닉네임을 설정해 주세요</TX.Headline2>

        <Input
          value={nickname}
          maxLength={12}
          placeholder="하꼬봇"
          inputStyleType={INPUT_STYLE_TYPES.OUTLINED}
          containerStyle={{
            marginTop: 44,
          }}
          onChange={onChangeNickname}
          onKeyUp={handleNicknameCheck}
          onKeyDown={handleNicknameCheck}
        />

        <NicknameInfoText>
          *10자 이내, 특수문자 불가. <br />
          서울2033 닉네임과 별도로 생성되며, 수정할 수 없습니다.
        </NicknameInfoText>

        <Button text="확인" disabled={!nickname.length} onClick={handleNicknameCreateButtonClick} />
      </NicknameContainer>

      <SnackBar ref={snackBarRef} />
    </>
  );
};

const FlexAlignCenterDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NicknameContainer = styled(FlexAlignCenterDiv)`
  flex-direction: column;
  width: 100%;
  height: 100vh;
`;

const NicknameInfoText = styled(TX.Caption1)`
  color: ${({ theme }) => theme.colors.ui_on_3};
  text-align: center;

  margin: 8px 0px 40px 0px;
`;

const DeleteButtonIcon = styled(DeleteSVG)`
  width: 16px;
  height: 16px;

  position: absolute;
  top: 26px;
  right: 24.5px;

  cursor: pointer;
  stroke: ${({ theme }) => theme.colors.black};
`;

export default Nickname;
